import type { Exercise } from '../../types.ts';
export const batch2Foundations: Exercise[] = [
  {
    id: 'foundations-civics',
    category: 'foundations',
    title: 'Federal powers and checks',
    topic: 'US constitutional structure',
    difficulty: 'Challenging',
    reading: {
      title: 'Dividing public power',
      sources: [
        {
          title: 'US Constitution · National Archives',
          url: 'https://www.archives.gov/founding-docs/constitution-transcript',
        },
        {
          title: 'Amendments 11–27 · National Archives',
          url: 'https://www.archives.gov/founding-docs/amendments-11-27',
        },
      ],
      sections: [
        {
          id: 'structure',
          title: 'Two different divisions of power',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The US constitutional system divides authority in two directions. Separation of powers distributes federal responsibilities among legislative, executive and judicial institutions. Federalism distributes authority between the federal government and the states. A disagreement between Congress and the president concerns the first division; a disagreement about whether a state or the national government may act concerns the second. Mixing these distinctions makes institutional questions harder than they need to be.',
            },
            {
              kind: 'paragraph',
              text: 'Article I vests the legislative powers granted by the Constitution in Congress. Article II places executive power in the president, and Article III establishes a Supreme Court and permits Congress to establish lower federal courts. These broad assignments do not mean that each institution acts in isolation. The design deliberately makes some actions depend on another institution’s participation.',
            },
            {
              kind: 'paragraph',
              text: 'Congress has enumerated powers, including powers concerning taxation and interstate commerce. The Tenth Amendment reserves powers not delegated to the United States, nor prohibited to the states, to the states or the people. This is not a rule that states can disregard valid federal law. Under the Supremacy Clause, the Constitution and federal laws made pursuant to it take priority over conflicting state law. Whether a federal law is constitutionally authorized remains a separate question.',
            },
            {
              kind: 'table',
              caption: 'Keep the questions separate',
              headers: ['Question', 'Relevant distinction'],
              rows: [
                ['Who legislates, executes or adjudicates?', 'Separation of powers'],
                ['Which level of government has authority?', 'Federalism'],
                ['Which valid rule prevails in a conflict?', 'Supremacy'],
              ],
            },
          ],
        },
        {
          id: 'legislation',
          title: 'A bill needs more than one decision',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Congress consists of the House of Representatives and the Senate. For an ordinary federal bill, both chambers must agree to the same text before it is presented to the president. Approval in one chamber is not enactment. The two-chamber requirement creates an opportunity to block or revise proposals before they become law.',
            },
            {
              kind: 'paragraph',
              text: 'The president may sign a presented bill or return it with objections, commonly called a veto. Congress can override a returned veto with a two-thirds vote in each chamber. A two-thirds vote in only one chamber is insufficient. The Constitution also describes circumstances in which a bill becomes law without a signature and a situation in which adjournment prevents its return. The questions here use an explicitly returned veto to avoid that separate timing issue.',
            },
            {
              kind: 'example',
              title: 'Different thresholds',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose a returned bill receives enough votes to override in the House but only a simple majority in the Senate. The override fails: the requirement applies to each chamber independently. Adding the votes from both chambers into one national percentage would apply a rule the Constitution does not specify.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The Senate and House also represent differently. Every state has two senators. House representation is apportioned among states according to population under constitutional and statutory rules. A state’s larger population does not give it extra senators. This distinction concerns seats, not a claim that every legislator has identical procedural influence.',
            },
          ],
        },
        {
          id: 'checks',
          title: 'Shared actions have different stages',
          blocks: [
            {
              kind: 'paragraph',
              text: 'For Supreme Court appointments, the president nominates and the Senate supplies advice and consent. The House does not perform the confirmation vote. This shared procedure gives two institutions distinct roles; nomination alone does not finish an appointment. Similarly, the president makes treaties with the advice and consent of the Senate, provided two-thirds of senators present concur. That treaty threshold is not the same wording as a veto override.',
            },
            {
              kind: 'paragraph',
              text: 'Impeachment also separates accusation from trial. The House has the sole power of impeachment; the Senate has the sole power to try impeachments. Conviction requires two-thirds of senators present. For a president, conviction on impeachment results in removal. Impeachment itself does not mean conviction, removal, or a criminal sentence.',
            },
            {
              kind: 'example',
              title: 'Read the institutional verb',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A report that the House has impeached an officer establishes that the accusation stage has occurred. It does not establish how the Senate will decide. To infer removal, you need the additional stage required by the Constitution. The distinction resembles a charge and a judgment, although impeachment is a constitutional process rather than an ordinary criminal prosecution.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Article III judges hold office during good behavior, rather than for a fixed four- or six-year elected term. This tenure is intended to separate their service from ordinary election cycles, while constitutional removal mechanisms still exist. “Independent” therefore does not mean that judges have unlimited authority or are immune from every form of accountability.',
            },
          ],
        },
        {
          id: 'change',
          title: 'Changing the framework itself',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An ordinary statute works within the Constitution; an amendment changes the constitutional text. Article V provides routes for proposing amendments and for ratifying them. One proposal route is a two-thirds vote of both houses of Congress. Ratification then requires three-fourths of the states, through legislatures or conventions as Congress specifies. Passing the proposal threshold does not complete ratification.',
            },
            {
              kind: 'paragraph',
              text: 'Article V also provides for a convention called on applications from two-thirds of state legislatures. The existence of more than one route is why a question must specify which stage and route it means. The president has no constitutional signing or veto role in the Article V amendment process. Do not automatically import the ordinary-bill procedure into constitutional amendment questions.',
            },
            {
              kind: 'paragraph',
              text: 'The daily questions apply these distinctions to ordinary bills, returned vetoes, appointments, impeachment, representation and amendments. Each asks which institution or stage matters. A reliable reading method is to identify the action first, then the decision maker, then any separate approval and threshold. This avoids treating every supermajority as interchangeable or every vote as final.',
            },
            {
              kind: 'paragraph',
              text: 'For example, if both chambers propose an amendment, it has cleared a national proposal stage but still needs state ratification. If a state law conflicts with a valid federal statute, the conflict is resolved through supremacy rather than by giving every state a veto over federal legislation. The framework supplies different tools for different institutional problems.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b2-civ-1',
        prompt:
          'A proposal has passed the House but has not been considered by the Senate. In the ordinary federal lawmaking process, what is still required before presentation to the president?',
        options: [
          {
            id: '0',
            text: 'A nationwide referendum',
          },
          {
            id: '1',
            text: 'Senate agreement to the same text',
          },
          {
            id: '2',
            text: 'A Supreme Court advisory vote',
          },
          {
            id: '3',
            text: 'Approval from three-fourths of states',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Both chambers must agree to the same bill before presentment. State ratification belongs to amendments, not ordinary bills.',
        sectionIds: ['structure', 'legislation'],
        evidence:
          'Both chambers must agree to the same bill before presentment. State ratification belongs to amendments, not ordinary bills.',
      },
      {
        id: 'b2-civ-2',
        prompt:
          'The president returns a bill with a veto. The House reaches the override threshold, but the Senate votes only by a simple majority. What follows?',
        options: [
          {
            id: '0',
            text: 'The bill passes because Congress is counted as one body.',
          },
          {
            id: '1',
            text: 'The Supreme Court decides whether to override.',
          },
          {
            id: '2',
            text: 'The House vote alone makes the bill law.',
          },
          {
            id: '3',
            text: 'The override fails because both chambers must reach the threshold.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'A returned veto requires two-thirds in each chamber; one chamber cannot compensate for the other.',
        sectionIds: ['legislation'],
        evidence:
          'A returned veto requires two-thirds in each chamber; one chamber cannot compensate for the other.',
      },
      {
        id: 'b2-civ-3',
        prompt:
          'Which pairing correctly identifies the roles in appointing a Supreme Court justice?',
        options: [
          {
            id: '0',
            text: 'President nominates; Senate gives advice and consent',
          },
          {
            id: '1',
            text: 'House nominates; president confirms',
          },
          {
            id: '2',
            text: 'Supreme Court nominates; House confirms',
          },
          {
            id: '3',
            text: 'Senate nominates; states ratify',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Article II pairs presidential nomination with Senate advice and consent; the House has no confirmation vote.',
        sectionIds: ['structure', 'checks'],
        evidence:
          'Article II pairs presidential nomination with Senate advice and consent; the House has no confirmation vote.',
      },
      {
        id: 'b2-civ-4',
        prompt:
          'A president has been impeached by the House. Which conclusion is justified from that fact alone?',
        options: [
          {
            id: '0',
            text: 'The president has already been removed.',
          },
          {
            id: '1',
            text: 'The president has been criminally convicted.',
          },
          {
            id: '2',
            text: 'The constitutional accusation stage has occurred.',
          },
          {
            id: '3',
            text: 'Two-thirds of senators have voted to convict.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Impeachment is the House accusation stage; Senate trial and conviction are separate.',
        sectionIds: ['checks'],
        evidence:
          'Impeachment is the House accusation stage; Senate trial and conviction are separate.',
      },
      {
        id: 'b2-civ-5',
        prompt:
          'A state’s population doubles while another state’s population stays constant. Which statement about Senate representation remains correct?',
        options: [
          {
            id: '0',
            text: 'The growing state automatically doubles its senators.',
          },
          {
            id: '1',
            text: 'Each state still has two senators.',
          },
          {
            id: '2',
            text: 'Senate seats transfer at the next census.',
          },
          {
            id: '3',
            text: 'Both states receive new seats if the national population rises.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Senate representation is equal by state; House apportionment uses population.',
        sectionIds: ['legislation'],
        evidence: 'Senate representation is equal by state; House apportionment uses population.',
      },
      {
        id: 'b2-civ-6',
        prompt:
          'Which disagreement most directly concerns federalism rather than separation of powers within the federal government?',
        options: [
          {
            id: '0',
            text: 'House and Senate disagree over a bill.',
          },
          {
            id: '1',
            text: 'The Senate rejects a presidential nominee.',
          },
          {
            id: '2',
            text: 'A president vetoes congressional legislation.',
          },
          {
            id: '3',
            text: 'A state challenges whether Congress has authority over an activity.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Federalism concerns the national/state allocation of authority; the other cases concern federal institutions.',
        sectionIds: ['structure'],
        evidence:
          'Federalism concerns the national/state allocation of authority; the other cases concern federal institutions.',
      },
      {
        id: 'b2-civ-7',
        prompt:
          'A state statute conflicts with a federal statute that is validly enacted under constitutional authority. Which rule addresses the conflict?',
        options: [
          {
            id: '0',
            text: 'The federal statute takes priority under the Supremacy Clause.',
          },
          {
            id: '1',
            text: 'The newer statute wins regardless of government level.',
          },
          {
            id: '2',
            text: 'The state statute wins inside that state.',
          },
          {
            id: '3',
            text: 'Each state must ratify the federal statute first.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'The premise establishes a valid federal law; supremacy gives it priority over conflicting state law.',
        sectionIds: ['structure'],
        evidence:
          'The premise establishes a valid federal law; supremacy gives it priority over conflicting state law.',
      },
      {
        id: 'b2-civ-8',
        prompt:
          'Both houses of Congress reach the Article V threshold to propose an amendment. Which additional step is required for this proposal to become part of the Constitution?',
        options: [
          {
            id: '0',
            text: 'Presidential signature',
          },
          {
            id: '1',
            text: 'Ratification by a simple majority of states',
          },
          {
            id: '2',
            text: 'Ratification by three-fourths of states',
          },
          {
            id: '3',
            text: 'A unanimous Supreme Court vote',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Proposal and ratification are distinct; Article V requires three-fourths of states for ratification.',
        sectionIds: ['change'],
        evidence:
          'Proposal and ratification are distinct; Article V requires three-fourths of states for ratification.',
      },
      {
        id: 'b2-civ-9',
        prompt: 'Which description of Article III federal judges’ tenure matches the Constitution?',
        options: [
          {
            id: '0',
            text: 'A six-year term renewable by the Senate',
          },
          {
            id: '1',
            text: 'Service during good behavior, rather than a fixed elected term',
          },
          {
            id: '2',
            text: 'A term ending with the nominating president’s term',
          },
          {
            id: '3',
            text: 'A life term immune from impeachment',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Good-behavior tenure is not a fixed elected term and does not eliminate constitutional removal.',
        sectionIds: ['checks'],
        evidence:
          'Good-behavior tenure is not a fixed elected term and does not eliminate constitutional removal.',
      },
      {
        id: 'b2-civ-10',
        prompt:
          'Which procedure does NOT give the president a constitutional signing or veto role?',
        options: [
          {
            id: '0',
            text: 'An ordinary bill passed by both chambers',
          },
          {
            id: '1',
            text: 'A revenue bill passed by both chambers',
          },
          {
            id: '2',
            text: 'An appropriations bill passed by both chambers',
          },
          {
            id: '3',
            text: 'An Article V constitutional amendment',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Article V uses proposal and state ratification; it does not use presidential presentment.',
        sectionIds: ['legislation', 'change'],
        evidence:
          'Article V uses proposal and state ratification; it does not use presidential presentment.',
      },
    ],
  },
  {
    id: 'foundations-markets',
    category: 'foundations',
    title: 'Prices, supply, and demand',
    topic: 'Competitive market model',
    difficulty: 'Challenging',
    reading: {
      title: 'Why prices and quantities change',
      sources: [
        {
          title: 'OpenStax · Demand, supply and equilibrium',
          url: 'https://openstax.org/books/principles-economics-3e/pages/3-1-demand-supply-and-equilibrium-in-markets-for-goods-and-services',
        },
        {
          title: 'OpenStax · Shifts in demand and supply',
          url: 'https://openstax.org/books/principles-economics-3e/pages/3-2-shifts-in-demand-and-supply-for-goods-and-services',
        },
      ],
      sections: [
        {
          id: 'curves',
          title: 'Start with a price and a decision',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Imagine several shops renting identical bicycles for a one-hour ride in the same town. We want to understand how many rides people would pay for during a week, and how many rentals the shops would offer. If the rental price changed, those decisions might change too. A market model is a deliberately simplified account of those decisions; it helps us ask which change could explain a higher price or fewer rentals.',
            },
            {
              kind: 'paragraph',
              text: 'Begin with the customers. At $30 per rental, some people might walk instead. At $10, more trips may be worth paying for. The numbers below are invented totals across all customers, not predictions about a particular town. Each row asks a separate “what if?” question about the same week. We are not adding the rows together or watching three successive weeks.',
            },
            {
              kind: 'table',
              caption: 'Customer plans for one week at three possible prices',
              headers: ['Price per one-hour rental', 'Rentals customers would pay for that week'],
              rows: [
                ['$10', '90'],
                ['$20', '60'],
                ['$30', '30'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'For example, the middle row says: if the price were $20, customers would be willing and able to pay for 60 rentals that week. Economists call that number the quantity demanded at $20. Demand means the whole set of price-and-quantity possibilities, rather than one row.',
            },
            {
              kind: 'paragraph',
              text: 'A demand schedule is simply a table like this one. Here “schedule” does not mean a timetable or a booking calendar. The technical name labels something we can already read: how many purchases people plan at each possible price.',
            },
            {
              kind: 'paragraph',
              text: 'For this comparison, imagine the same customers, incomes, weather and transport alternatives in each row. Only the rental price changes. That is what “other things equal” means here. If a new train service opened, for example, we might need a different table.',
            },
            {
              kind: 'paragraph',
              text: 'We can draw the table as a graph. The horizontal axis counts rentals per week: farther right means more rentals. The vertical axis measures dollars per rental: higher means a higher price. To place the $20 row, go across to 60 and up to $20. The dot represents both values together. Connecting the dots gives a demand curve; economists use “curve” even when the drawn line is straight.',
            },
            {
              kind: 'diagram',
              drawing: 'market-demand',
              caption:
                'Each dot is one row of the customer table. Follow the guides from 60 rentals and $20 to their shared dot.',
              description:
                'Price in dollars per rental is vertical, from 0 to 40. Weekly rentals are horizontal, from 0 to 120. Demand joins (30 rentals, $30), (60, $20), and (90, $10). Dashed guides locate the middle dot at 60 rentals and $20.',
            },
            {
              kind: 'paragraph',
              text: 'Now compare the $30 and $20 rows. The lower price goes with more planned rentals. On the graph we move down and right along the same line. There is no new table: both possibilities were in the original one. That is the meaning of a movement along a demand curve. The line slopes down because, in this example, higher prices go with fewer purchases.',
            },
          ],
        },
        {
          id: 'balance',
          title: 'Bring the shops into the same picture',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The shops face a different decision: how many one-hour rentals are worth offering at each price? Staff, repairs and additional bicycles cost money. A higher rental price can make extra opening hours or additional bicycles worth paying for. We assume shops can adjust their offerings over the period being modeled; we are not describing a shop with a permanently fixed number of slots.',
            },
            {
              kind: 'paragraph',
              text: 'Supply is the sellers’ corresponding set of plans. A supply schedule is a table of quantities sellers would offer at different prices; quantity supplied is a single row’s amount. To compare customers and shops, both columns below count one-hour rentals in the same town during the same week.',
            },
            {
              kind: 'table',
              caption: 'Both sides considering the same possible rental prices',
              headers: ['Price per rental', 'Customers would buy', 'Shops would offer'],
              rows: [
                ['$10', '90 rentals/week', '30 rentals/week'],
                ['$20', '60 rentals/week', '60 rentals/week'],
                ['$30', '30 rentals/week', '90 rentals/week'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'At $10, customers want 90 rentals but shops plan only 30. The gap is 90 − 30 = 60 rentals: a shortage at that price. It does not mean that 90 rentals actually occur. Some customers cannot obtain the rental they wanted. At $30, shops offer 90 but customers want 30. The 60 unwanted rental slots are a surplus at that price.',
            },
            {
              kind: 'paragraph',
              text: 'At $20, both sides plan 60 rentals. Economists call this matching price the equilibrium price, and 60 the equilibrium quantity. “Equilibrium” means balance here: the planned amount bought equals the planned amount sold. The supply and demand curves meet at the point representing that pair.',
            },
            {
              kind: 'diagram',
              drawing: 'market-balance',
              caption:
                'At the crossing, customers and shops agree on both the price ($20) and the weekly number of rentals (60).',
              description:
                'Demand slopes downward through 30 rentals at $30, 60 at $20 and 90 at $10. Supply slopes upward through 30 rentals at $10, 60 at $20 and 90 at $30. Their crossing is 60 weekly rentals at $20 each.',
            },
            {
              kind: 'paragraph',
              text: 'Why might prices move toward that crossing? With unused slots, competing shops have a reason to lower their prices to attract customers. With more customers than available slots, shops may be able to charge more. These pressures explain the model’s adjustment; the crossing is not a magic rule that forces a real market to balance instantly.',
            },
            {
              kind: 'paragraph',
              text: 'For the examples that follow, assume many competing buyers and sellers, comparable rentals, customer purchases falling as price rises, and shop offerings rising as price rises. Prices can adjust unless a question states a legal restriction. This model leaves out differences in bike quality, a dominant shop’s pricing power and many other real complications.',
            },
          ],
        },
        {
          id: 'shifts',
          title: 'Changing a row versus changing the whole table',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Return to the original customer table. A price drop from $30 to $20 changes which row we use. Now consider a different event: a new attraction draws more visitors to town. At an unchanged price of $20, customers would now pay for 90 rentals instead of 60. The old $20 row no longer describes their plans. We need a new demand table.',
            },
            {
              kind: 'table',
              caption: 'More visitors: compare the same price before and after',
              headers: [
                'Price per rental',
                'Original customer plans',
                'Customer plans with more visitors',
              ],
              rows: [
                ['$10', '90 rentals/week', '120 rentals/week'],
                ['$20', '60 rentals/week', '90 rentals/week'],
                ['$30', '30 rentals/week', '60 rentals/week'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'Every row now has 30 more planned rentals. On the graph, keep your finger at the same price and move horizontally to the new quantity. Because quantities increase to the right, this change is called a rightward shift of demand. Fewer planned purchases at each price would be a leftward shift. Left and right describe the picture, not different kinds of customers or directions in which money moves.',
            },
            {
              kind: 'diagram',
              drawing: 'market-shift',
              caption:
                'At the same $20 price, the visitors change planned purchases from 60 to 90. The arrow is horizontal because the comparison holds price fixed.',
              description:
                'Original demand has 60 rentals at $20; new demand has 90 at $20. The new demand line is to the right of the original at each price. A horizontal arrow at $20 joins those two points. This is a comparison of demand plans, not the final market equilibrium.',
            },
            {
              kind: 'paragraph',
              text: 'That horizontal comparison identifies what changed first. It does not say the eventual price stays at $20. At that price the shops still offer only 60 rentals while customers now want 90: there is a shortage. As the price rises, shops offer more and some customers decide against renting. With unchanged supply, the new meeting point is a higher price and a larger number of rentals.',
            },
            {
              kind: 'example',
              title: 'A separate change: cheaper maintenance',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose instead that customers’ plans stay as they were, but a repair method cuts the shops’ costs. Shops can profitably offer more rentals at the old $20 price. That changes the supply table, not the customer table.',
                },
                {
                  kind: 'paragraph',
                  text: 'At the old price, there are now more rental slots than customers want. Shops compete for customers by lowering the price. At the lower price customers buy more rentals along their existing demand relationship. The result is a lower matching price and more rentals. If repairs became more expensive, reverse the steps: fewer slots at the old price, a shortage, then a higher price and fewer rentals.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Use this method before memorizing graph directions: identify whose plans change at the same price, check for a shortage or surplus at the old price, then follow the responses to a new matching price. A change in actual purchases alone does not tell you that the demand table changed.',
            },
            {
              kind: 'table',
              caption: 'Results after one change, with the other side’s plans unchanged',
              headers: [
                'Initial change at each price',
                'New matching price',
                'New number bought and sold',
              ],
              rows: [
                ['Customers want more', 'Higher', 'Larger'],
                ['Customers want fewer', 'Lower', 'Smaller'],
                ['Sellers offer more', 'Lower', 'Larger'],
                ['Sellers offer fewer', 'Higher', 'Smaller'],
              ],
            },
            {
              kind: 'paragraph',
              text: 'For the production-cost questions, apply the shop reasoning to the named product. Ask what its sellers can offer at the old selling price after their costs change. Do not call a later fall in purchases a change in customers’ preferences when it can be explained by the new price.',
            },
          ],
        },
        {
          id: 'related',
          title: 'Why customers might change their plans',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Customers can change their plans even when this product’s price stays fixed. Income and the prices of other products are two possible reasons. These are sometimes called demand determinants: circumstances that help determine the quantities in the table. Naming the circumstance is more useful than memorizing that label.',
            },
            {
              kind: 'paragraph',
              text: 'For a normal good, higher income leads people to buy more at each unchanged price. “Normal” is the name of that income response, not a claim about whether the product is ordinary or good quality. For example, households with more money might take more paid leisure trips at the same ticket prices. Some products instead see lower demand as incomes rise; economists call those inferior goods. The label alone says nothing about their safety or craftsmanship.',
            },
            {
              kind: 'paragraph',
              text: 'Products that can serve in place of one another are substitutes. Imagine a commuter choosing between two bus companies on the same route. If one company raises its fare, some passengers switch to the other even though the other fare is unchanged. The second company’s demand table changes. In the tea-and-coffee question, the relevant assumption is that buyers are willing to switch between those drinks.',
            },
            {
              kind: 'paragraph',
              text: 'Products used together are complements. Imagine cameras that need a particular film cartridge. If more expensive cameras lead to fewer camera purchases and less film use, buyers want fewer cartridges at the same cartridge prices. In the printer question, track the same sequence from fewer printer purchases to less associated ink use. We hold other influences on ink purchases fixed; existing printer owners do not all stop printing overnight.',
            },
          ],
        },
        {
          id: 'limits',
          title: 'What the model does—and does not—tell us',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Consider a market where customer plans increase at every price and seller offerings also increase at every price. Separately, more customer demand tends to raise the matching price; more supply tends to lower it. Those price effects oppose each other. Both changes favor a larger number of transactions. Under the upward-supply, downward-demand assumptions used here, the new quantity is larger, but we need the size of the changes to determine the new price.',
            },
            {
              kind: 'example',
              title: 'Why a price increase does not identify its cause',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'In the bicycle market, an influx of visitors can raise the price because more customers want rentals. Expensive repairs can also raise the price because shops offer fewer rentals. One story predicts more rentals and the other fewer. If a news report tells us only that the rental price rose, both stories are still possible. We need another observation, such as costs, visitor numbers or transaction counts, to distinguish them.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'A price ceiling is a legal maximum price. Think of it as a rule that a posted rental price cannot exceed a stated amount. It is different from a change in what customers want or what a rental costs the shops to provide.',
            },
            {
              kind: 'example',
              title: 'A maximum that does and does not restrict the price',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Use the earlier rental table, where both sides plan 60 rentals at $20. A legal maximum of $25 still permits the $20 price. The rule does not change the model’s outcome; it is called nonbinding because it does not restrict that outcome.',
                },
                {
                  kind: 'paragraph',
                  text: 'A maximum of $10 prevents the $20 price. At the allowed $10 price, customers want 90 rentals and shops offer 30. The shortage is 60 rentals. This ceiling is binding: it rules out the price at which the plans would match. Waiting lists or other allocation rules might determine who gets the available rentals, but the table alone cannot tell us which customers succeed.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'When answering a legal-maximum question, first compare the maximum with the price that would otherwise match buyers and sellers. Do not infer a shortage merely from the word “ceiling.” Likewise, do not infer its fairness or overall policy merit from this simple calculation. The model describes the stated price and quantity relationships, not every consequence of a policy.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b2-econ-1',
        prompt:
          'A bicycle rental shop lowers its price. Customers’ incomes, preferences and other travel options stay the same. We are comparing how many rentals customers would buy at different prices. Which description distinguishes this price change from a change in their underlying buying plans?',
        options: [
          {
            id: '0',
            text: 'Customers now want more rentals at every price, including prices that did not change.',
          },
          {
            id: '1',
            text: 'Shops now offer fewer rentals at every price.',
          },
          {
            id: '2',
            text: 'Customers choose a larger quantity at the lower price, using the same price-and-quantity relationship.',
          },
          {
            id: '3',
            text: 'Customers now want fewer rentals at every price, including prices that did not change.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'A lower price selects a different row of the same demand table. It does not change how many rentals customers would want at an unchanged price. That distinction is called a movement along demand rather than a shift of demand.',
        sectionIds: ['curves'],
        evidence:
          'A lower price selects a different row of the same demand table. It does not change how many rentals customers would want at an unchanged price. That distinction is called a movement along demand rather than a shift of demand.',
      },
      {
        id: 'b2-econ-2',
        prompt:
          'Many competing shops sell the same kind of product. Buyers normally purchase fewer units when its price rises; sellers normally offer more. A new method lowers production costs for every shop, while buyers’ preferences and budgets are unchanged. After prices adjust, how do the price and the number bought and sold compare with before?',
        options: [
          {
            id: '0',
            text: 'Higher price and more units sold',
          },
          {
            id: '1',
            text: 'Lower price and more units sold',
          },
          {
            id: '2',
            text: 'Lower price and fewer units sold',
          },
          {
            id: '3',
            text: 'Higher price and fewer units sold',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Lower costs let sellers offer more at the old price. That creates excess offerings relative to unchanged customer plans. Competition pushes the price down; at the lower price customers buy more. The new balance has a lower price and a larger quantity.',
        sectionIds: ['curves', 'balance', 'shifts'],
        evidence:
          'Lower costs let sellers offer more at the old price. That creates excess offerings relative to unchanged customer plans. Competition pushes the price down; at the lower price customers buy more. The new balance has a lower price and a larger quantity.',
      },
      {
        id: 'b2-econ-3',
        prompt:
          'At a farmers’ market, apples cost $5 per bag. At that price, customers want to buy 140 bags today and sellers have 90 bags available. What gap is there between the quantities buyers want and sellers offer?',
        options: [
          {
            id: '0',
            text: 'A shortage of 50 bags',
          },
          {
            id: '1',
            text: 'A surplus of 50 bags',
          },
          {
            id: '2',
            text: 'A shortage of 230 bags',
          },
          {
            id: '3',
            text: 'A surplus of 230 bags',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Customers want 140 bags but only 90 are offered. The 50-bag shortfall is a shortage at the stated price. Adding the two quantities would not measure how many desired purchases cannot be filled.',
        sectionIds: ['curves', 'balance'],
        evidence:
          'Customers want 140 bags but only 90 are offered. The 50-bag shortfall is a shortage at the stated price. Adding the two quantities would not measure how many desired purchases cannot be filled.',
      },
      {
        id: 'b2-econ-4',
        prompt:
          'Economists call a product a “normal good” if people tend to buy more of it when their incomes rise. Household incomes rise, but this product’s price, sellers’ costs and other conditions stay the same. Which description correctly locates the change in the market?',
        options: [
          {
            id: '0',
            text: 'Buyers choose fewer units solely because the product’s price changed.',
          },
          {
            id: '1',
            text: 'Sellers offer more units at every price because their production costs changed.',
          },
          {
            id: '2',
            text: 'Buyers want fewer units at each price than before.',
          },
          {
            id: '3',
            text: 'Buyers want more units at each price than before.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The change is in buyers’ plans at unchanged prices: the demand relationship changes. It is not a response to a new price of this product or to lower production costs.',
        sectionIds: ['curves', 'balance', 'shifts', 'related'],
        evidence:
          'The change is in buyers’ plans at unchanged prices: the demand relationship changes. It is not a response to a new price of this product or to lower production costs.',
      },
      {
        id: 'b2-econ-5',
        prompt:
          'For these customers, tea and coffee can serve in place of each other. Tea becomes more expensive. Coffee’s price, customers’ incomes and their preferences otherwise stay unchanged. How does the model predict customers’ willingness to buy coffee at each coffee price will change?',
        options: [
          {
            id: '0',
            text: 'They want less coffee at each price.',
          },
          {
            id: '1',
            text: 'Their coffee purchases cannot change unless coffee’s own price changes.',
          },
          {
            id: '2',
            text: 'They want more coffee at each price.',
          },
          {
            id: '3',
            text: 'Only coffee sellers’ production costs change.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Tea is now a more expensive alternative, so some purchases move to coffee. Buyers want more coffee even at an unchanged coffee price. Products that buyers can switch between in this way are called substitutes.',
        sectionIds: ['curves', 'related'],
        evidence:
          'Tea is now a more expensive alternative, so some purchases move to coffee. Buyers want more coffee even at an unchanged coffee price. Products that buyers can switch between in this way are called substitutes.',
      },
      {
        id: 'b2-econ-6',
        prompt:
          'Printers and their compatible ink are used together. Higher printer prices lead to fewer printer purchases, while ink prices and other influences on ink use stay unchanged. What change in customers’ plans to buy ink does this model predict?',
        options: [
          {
            id: '0',
            text: 'Less ink wanted at each ink price, because fewer new printers need it',
          },
          {
            id: '1',
            text: 'More ink wanted at each ink price, because ink is cheaper relative to printers',
          },
          {
            id: '2',
            text: 'No change in ink purchases unless ink’s own price changes',
          },
          {
            id: '3',
            text: 'More ink offered by sellers at each price, because printer prices rose',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Fewer new printers reduce associated ink use, so buyers want less ink at an unchanged ink price. This changes demand for ink. It does not mean existing printer owners stop buying ink or that ink-production costs changed.',
        sectionIds: ['curves', 'related'],
        evidence:
          'Fewer new printers reduce associated ink use, so buyers want less ink at an unchanged ink price. This changes demand for ink. It does not mean existing printer owners stop buying ink or that ink-production costs changed.',
      },
      {
        id: 'b2-econ-7',
        prompt:
          'In a competitive market, buyers purchase fewer units at higher prices and sellers offer more. Now two things change: buyers want more at every price, and sellers offer more at every price. Prices can adjust. Without knowing the sizes of those two changes, what can we conclude about the new price and number of units bought and sold?',
        options: [
          {
            id: '0',
            text: 'Price rises; the number sold is uncertain.',
          },
          {
            id: '1',
            text: 'Price falls and the number sold falls.',
          },
          {
            id: '2',
            text: 'Price stays the same and the number sold rises.',
          },
          {
            id: '3',
            text: 'The number sold rises; the price direction is uncertain.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'More demand alone pushes price upward; more supply alone pushes it downward. Both support more transactions. Their relative sizes determine the net price change, so there is no basis for assuming they cancel exactly.',
        sectionIds: ['curves', 'balance', 'shifts', 'limits'],
        evidence:
          'More demand alone pushes price upward; more supply alone pushes it downward. Both support more transactions. Their relative sizes determine the net price change, so there is no basis for assuming they cancel exactly.',
      },
      {
        id: 'b2-econ-8',
        prompt:
          'Buyers’ and sellers’ plans currently match at a price of $20 per item. A new law sets a maximum legal price—a price ceiling—of $25. No other market conditions change. In this simple market model, what is the immediate effect?',
        options: [
          {
            id: '0',
            text: 'Buyers must face a shortage of items.',
          },
          {
            id: '1',
            text: 'The existing $20 price remains allowed, so the maximum does not constrain it.',
          },
          {
            id: '2',
            text: 'Sellers must face a surplus of items.',
          },
          {
            id: '3',
            text: 'Buyers want fewer items at every price, even though their circumstances have not changed.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'A maximum of $25 forbids prices above $25; it does not force sellers to charge $25. The matching $20 price remains legal. This ceiling is nonbinding.',
        sectionIds: ['curves', 'balance', 'limits'],
        evidence:
          'A maximum of $25 forbids prices above $25; it does not force sellers to charge $25. The matching $20 price remains legal. This ceiling is nonbinding.',
      },
      {
        id: 'b2-econ-9',
        prompt:
          'Many competing bakeries sell comparable loaves. The flour needed for each loaf becomes more expensive for every bakery. Customers’ willingness to buy at each bread price is unchanged, and customers buy fewer loaves when prices rise. After prices adjust, what does the model predict?',
        options: [
          {
            id: '0',
            text: 'Higher price per loaf and fewer loaves sold',
          },
          {
            id: '1',
            text: 'Lower price per loaf and more loaves sold',
          },
          {
            id: '2',
            text: 'Higher price per loaf and more loaves sold',
          },
          {
            id: '3',
            text: 'Lower price per loaf and fewer loaves sold',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'At the old bread price, higher flour costs make fewer loaves worth producing. With customer plans unchanged, that creates a shortage. The price rises, and customers then buy fewer loaves along their unchanged demand relationship.',
        sectionIds: ['curves', 'balance', 'shifts'],
        evidence:
          'At the old bread price, higher flour costs make fewer loaves worth producing. With customer plans unchanged, that creates a shortage. The price rises, and customers then buy fewer loaves along their unchanged demand relationship.',
      },
      {
        id: 'b2-econ-10',
        prompt:
          'A report says that a product’s market price rose after buyers’ and sellers’ plans settled into a new balance. It gives no information about sales, production costs or customers. In the competitive-market model, which conclusion is justified by the price observation alone?',
        options: [
          {
            id: '0',
            text: 'Buyers must want more at each price.',
          },
          {
            id: '1',
            text: 'Sellers must offer more at each price.',
          },
          {
            id: '2',
            text: 'Either stronger buying plans or reduced seller offerings could explain the higher price.',
          },
          {
            id: '3',
            text: 'The number of units bought and sold must have risen.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'More customers wanting the product and fewer units being offered by sellers can each raise price. They have different effects on the number sold. A price increase alone cannot distinguish them.',
        sectionIds: ['curves', 'balance', 'shifts', 'limits'],
        evidence:
          'More customers wanting the product and fewer units being offered by sellers can each raise price. They have different effects on the number sold. A price increase alone cannot distinguish them.',
      },
    ],
  },
  {
    id: 'foundations-sampling',
    category: 'foundations',
    title: 'Samples, experiments, and evidence',
    topic: 'Statistical inference',
    difficulty: 'Challenging',
    reading: {
      title: 'What a study can establish',
      sources: [
        {
          title: 'OpenStax · Data and sampling (CC BY 4.0)',
          url: 'https://openstax.org/books/introductory-statistics/pages/1-2-data-sampling-and-variation-in-data-and-sampling',
        },
        {
          title: 'OpenStax · Experimental design (CC BY 4.0)',
          url: 'https://openstax.org/books/introductory-statistics-2e/pages/1-4-experimental-design-and-ethics',
        },
      ],
      sections: [
        {
          id: 'sample',
          title: 'Define who the claim is about',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A population is the full group about which a question is asked. A sample is the part actually observed. A population quantity, such as the mean commute of all city workers, is a parameter; a quantity calculated from sampled workers is a statistic. The purpose of sampling is to learn about the population without observing everyone, but the inference depends on how the sample relates to that population.',
            },
            {
              kind: 'paragraph',
              text: 'A sampling frame is the operational list or procedure used to reach people. A list of landline numbers, for example, cannot reach workers who have no landline. Selecting randomly within an incomplete frame does not repair that omission. Undercoverage concerns who cannot be selected, while nonresponse concerns selected people who do not provide usable responses. Both may bias an estimate if the missing people differ in relevant ways.',
            },
            {
              kind: 'paragraph',
              text: 'Random sampling uses chance in selection rather than relying on convenient volunteers. In a simple random sample of a specified size, every subset of that size has the same chance. A large voluntary online poll does not become representative merely because many people respond. More observations can estimate the preferences of the responding group very precisely while still missing the target population.',
            },
          ],
        },
        {
          id: 'variation',
          title: 'Random noise and systematic error differ',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Even well-designed random samples vary from one draw to another. This is sampling variability. Larger independent random samples generally reduce that variability, but increasing sample size does not automatically remove systematic selection or measurement error. Precision describes how tightly repeated estimates cluster; accuracy also depends on whether they cluster around the right target.',
            },
            {
              kind: 'example',
              title: 'A campus lunch survey',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'A university wants the fraction of all students satisfied with lunch. It asks only people leaving one dining hall at noon. Surveying a thousand of those diners may give a stable estimate for that group, while missing students who avoid the hall because they dislike the food. Taking more of the same convenient sample does not fix the omitted group.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Stratified sampling divides a population into defined strata, such as year groups, and samples within each. It can ensure that smaller groups are represented. If strata are sampled at different rates, an overall estimate must respect their population sizes. Giving an oversampled small group equal influence would change the target being estimated.',
            },
            {
              kind: 'table',
              caption: 'Common design issues',
              headers: ['Problem', 'What it affects'],
              rows: [
                ['Incomplete frame', 'Who can be selected'],
                ['Nonresponse', 'Who supplies data after selection'],
                ['Leading wording', 'How responses are measured'],
                ['Ordinary random variation', 'Which estimate arises on this draw'],
              ],
            },
          ],
        },
        {
          id: 'causation',
          title: 'Selection and assignment answer different questions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Random selection helps support generalization to a population. Random assignment allocates study participants to treatments and helps support a causal comparison. These are distinct operations. Volunteers may be randomly assigned to treatment groups even though they were not randomly sampled from all people. Such a study can provide causal evidence for the studied setting while leaving broader generalization uncertain.',
            },
            {
              kind: 'paragraph',
              text: 'An observational study records existing conditions without assigning the exposure of interest. An experiment intervenes by assigning a treatment. When existing exposure is associated with an outcome, a confounding variable may influence both. For example, people who elect to join a study group might also be more motivated. Better exam scores among joiners would not by themselves identify the study group as the cause.',
            },
            {
              kind: 'paragraph',
              text: 'Random assignment makes treatment allocation unrelated to participant characteristics by design. It balances potential confounders on average across repeated assignments, not with an iron guarantee of identical groups in any one experiment. A control group provides a comparison under another condition. Blinding reduces changes in behavior or measurement caused by knowing group membership; it does not substitute for random assignment.',
            },
            {
              kind: 'example',
              title: 'A teaching experiment',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Volunteers are randomly assigned to a new lesson or the standard lesson and take the same test. The design is stronger for causal inference than letting them choose lessons. It does not automatically prove that the result applies equally to all ages, subjects or schools.',
                },
              ],
            },
          ],
        },
        {
          id: 'interpretation',
          title: 'Read the result at the design’s resolution',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A statistical association means that outcomes vary with an observed exposure; it does not specify the mechanism. Even a plausible causal story needs evidence capable of distinguishing it from alternatives. Confounding is not a claim that an observed relationship is fake. It is a reason why more than one explanation may fit the data.',
            },
            {
              kind: 'paragraph',
              text: 'For numerical summaries, distinguish counts from rates. If one group has more incidents but also many more people or much longer observation, raw counts may not establish greater individual risk. Specify the denominator: incidents per person, per journey, or per hour answer different questions. A fair comparison needs a denominator matched to the claim.',
            },
            {
              kind: 'paragraph',
              text: 'Weighted aggregation similarly requires the right group sizes. Suppose a small department has ten workers with an average commute of ten minutes, and a larger one has thirty workers averaging thirty minutes. The workforce average is (10 × 10 + 30 × 30)/40 = 25 minutes, not twenty. Twenty gives equal weight to departments rather than workers.',
            },
            {
              kind: 'paragraph',
              text: 'The daily questions ask what sampling, assignment, controls and weighting each establish. Before accepting a conclusion, identify the target population, who was observed, how exposure was assigned, and how the outcome was measured. A study can be strong on one dimension and weak on another: a representative observational survey may describe a population well without proving causation, while a randomized volunteer experiment may support causation without justifying unlimited generalization.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b2-stat-1',
        prompt:
          'A city estimates all residents’ transit satisfaction using only responses to a voluntary poll on a transit-enthusiast website. What is the central concern even with 50,000 responses?',
        options: [
          {
            id: '0',
            text: 'The volunteers may differ systematically from all residents.',
          },
          {
            id: '1',
            text: 'The sample is too large.',
          },
          {
            id: '2',
            text: 'The average cannot be calculated.',
          },
          {
            id: '3',
            text: 'Random assignment was not used to select the website.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Voluntary response from a specialized website can bias population inference; large size does not repair selection.',
        sectionIds: ['sample', 'variation'],
        evidence:
          'Voluntary response from a specialized website can bias population inference; large size does not repair selection.',
      },
      {
        id: 'b2-stat-2',
        prompt:
          'Researchers randomly select phone numbers from a list that excludes mobile-only households. Which issue remains?',
        options: [
          {
            id: '0',
            text: 'No randomness anywhere in the design',
          },
          {
            id: '1',
            text: 'Perfect representation because selection is random',
          },
          {
            id: '2',
            text: 'Undercoverage of the target population',
          },
          {
            id: '3',
            text: 'Confounding between assigned treatments',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Random selection within an incomplete frame leaves the excluded households unreachable.',
        sectionIds: ['sample'],
        evidence:
          'Random selection within an incomplete frame leaves the excluded households unreachable.',
      },
      {
        id: 'b2-stat-3',
        prompt:
          'A representative random sample is contacted, but selected people with long commutes rarely answer. What kind of problem threatens the commute estimate?',
        options: [
          {
            id: '0',
            text: 'Treatment spillover',
          },
          {
            id: '1',
            text: 'Blinding failure',
          },
          {
            id: '2',
            text: 'Equal-probability selection',
          },
          {
            id: '3',
            text: 'Nonresponse related to the quantity being measured',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Selected people with systematically different commutes fail to respond, creating a nonresponse concern.',
        sectionIds: ['sample', 'variation'],
        evidence:
          'Selected people with systematically different commutes fail to respond, creating a nonresponse concern.',
      },
      {
        id: 'b2-stat-4',
        prompt:
          'Volunteers are randomly assigned to two lesson types. Which statement best separates the design’s strengths and limits?',
        options: [
          {
            id: '0',
            text: 'It proves the result generalizes to every student.',
          },
          {
            id: '1',
            text: 'It strengthens causal comparison but does not make the volunteers a random population sample.',
          },
          {
            id: '2',
            text: 'It supports population description but cannot compare causes.',
          },
          {
            id: '3',
            text: 'It guarantees the groups have identical characteristics.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Assignment supports causal inference; selection governs representativeness. Randomization balances in expectation, not perfectly.',
        sectionIds: ['causation', 'interpretation'],
        evidence:
          'Assignment supports causal inference; selection governs representativeness. Randomization balances in expectation, not perfectly.',
      },
      {
        id: 'b2-stat-5',
        prompt:
          'Students who choose an optional study group get higher scores. More motivated students are also more likely to join. Why is motivation a concern?',
        options: [
          {
            id: '0',
            text: 'It must be caused by the higher scores.',
          },
          {
            id: '1',
            text: 'It makes scores impossible to measure.',
          },
          {
            id: '2',
            text: 'It guarantees the group has no effect.',
          },
          {
            id: '3',
            text: 'It may influence both participation and scores.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Motivation is a plausible confounder; its existence does not prove the group has zero effect.',
        sectionIds: ['causation'],
        evidence:
          'Motivation is a plausible confounder; its existence does not prove the group has zero effect.',
      },
      {
        id: 'b2-stat-6',
        prompt:
          'What is the main purpose of keeping outcome assessors unaware of treatment assignment?',
        options: [
          {
            id: '0',
            text: 'To prevent measurement from being influenced by group knowledge',
          },
          {
            id: '1',
            text: 'To choose a representative sample',
          },
          {
            id: '2',
            text: 'To ensure treatment groups are equal in size',
          },
          {
            id: '3',
            text: 'To eliminate all sampling variability',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Blinding reduces assessment bias from knowledge of assignment, a different function from sampling or randomization.',
        sectionIds: ['causation'],
        evidence:
          'Blinding reduces assessment bias from knowledge of assignment, a different function from sampling or randomization.',
      },
      {
        id: 'b2-stat-7',
        prompt:
          'A survey samples the same number from two strata, but one stratum contains nine times as many people in the population. How should an overall population estimate normally combine their results?',
        options: [
          {
            id: '0',
            text: 'Give each stratum half the weight.',
          },
          {
            id: '1',
            text: 'Use only the larger sample’s result.',
          },
          {
            id: '2',
            text: 'Weight by their population shares.',
          },
          {
            id: '3',
            text: 'Average only respondents with matching answers.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Equal sample sizes do not imply equal population sizes; population shares supply the aggregation weights.',
        sectionIds: ['variation', 'interpretation'],
        evidence:
          'Equal sample sizes do not imply equal population sizes; population shares supply the aggregation weights.',
      },
      {
        id: 'b2-stat-8',
        prompt:
          'Increasing the size of a well-designed independent random sample generally does which of the following?',
        options: [
          {
            id: '0',
            text: 'Eliminates a leading question’s wording effect',
          },
          {
            id: '1',
            text: 'Reduces sampling variability',
          },
          {
            id: '2',
            text: 'Guarantees the estimate equals the parameter',
          },
          {
            id: '3',
            text: 'Turns association into causation',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Larger random samples reduce random variation, not automatically selection, measurement or causal problems.',
        sectionIds: ['variation'],
        evidence:
          'Larger random samples reduce random variation, not automatically selection, measurement or causal problems.',
      },
      {
        id: 'b2-stat-9',
        prompt:
          'Ten workers average 10-minute commutes; thirty other workers average 30-minute commutes. What is the mean commute across all forty workers?',
        options: [
          {
            id: '0',
            text: '20 minutes',
          },
          {
            id: '1',
            text: '22.5 minutes',
          },
          {
            id: '2',
            text: '30 minutes',
          },
          {
            id: '3',
            text: '25 minutes',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Total commute time is 10×10 + 30×30 = 1000 minutes across forty workers, giving 25.',
        sectionIds: ['interpretation'],
        evidence:
          'Total commute time is 10×10 + 30×30 = 1000 minutes across forty workers, giving 25.',
      },
      {
        id: 'b2-stat-10',
        prompt:
          'A random sample of city residents shows that bicycle ownership is associated with shorter commutes. With no treatment assignment, which conclusion is best supported?',
        options: [
          {
            id: '0',
            text: 'There is an association in the sampled population; causal direction is not established.',
          },
          {
            id: '1',
            text: 'Buying a bicycle necessarily shortens a commute.',
          },
          {
            id: '2',
            text: 'Short commutes necessarily cause bicycle purchases.',
          },
          {
            id: '3',
            text: 'Representative sampling rules out confounding.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Random sampling supports population inference but does not replace a design that identifies causation.',
        sectionIds: ['sample', 'causation', 'interpretation'],
        evidence:
          'Random sampling supports population inference but does not replace a design that identifies causation.',
      },
    ],
  },
  {
    id: 'foundations-inheritance',
    category: 'foundations',
    title: 'Inheritance and probability',
    topic: 'One-gene inheritance models',
    difficulty: 'Challenging',
    reading: {
      title: 'From alleles to predictions',
      sources: [
        {
          title: 'OpenStax · Characteristics and traits (CC BY 4.0)',
          url: 'https://openstax.org/books/biology-2e/pages/12-2-characteristics-and-traits',
        },
      ],
      sections: [
        {
          id: 'alleles',
          title: 'Copies, variants, and expression',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A gene is a stretch of genetic information; an allele is a variant of a gene. In the diploid, single-gene model used here, an organism carries two alleles at the locus being studied, one inherited from each parent. A locus is a position in the genome. The allele pair is its genotype. The observable trait, produced through gene expression and potentially environmental influences, is its phenotype.',
            },
            {
              kind: 'paragraph',
              text: 'Homozygous means the two alleles match; heterozygous means they differ. In a complete-dominance model with alleles A and a, AA and Aa have the dominant phenotype while aa has the recessive phenotype. This is a rule relating genotype to phenotype. It does not say that A is more common, more beneficial, stronger, or more likely to be transmitted.',
            },
            {
              kind: 'paragraph',
              text: 'An organism with genotype Aa can carry a without displaying the recessive phenotype. The allele has not vanished; it can still enter a gamete and be passed on. That distinction explains why a trait absent from the appearance of both parents can appear in offspring. A phenotype alone may therefore leave more than one possible genotype.',
            },
            {
              kind: 'table',
              caption: 'Complete dominance at one locus',
              headers: ['Genotype', 'Description', 'Phenotype'],
              rows: [
                ['AA', 'Homozygous', 'Dominant'],
                ['Aa', 'Heterozygous', 'Dominant'],
                ['aa', 'Homozygous', 'Recessive'],
              ],
            },
          ],
        },
        {
          id: 'gametes',
          title: 'Build offspring from parental contributions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Gametes carry one allele at the locus. Under the Mendelian segregation assumption, an Aa parent produces A and a gametes with equal probability. An AA parent contributes A at that locus every time. Random union of gametes then determines an offspring’s pair. The usual Punnett square is a table of these possible unions, not a schedule allocating outcomes to a family.',
            },
            {
              kind: 'example',
              title: 'A heterozygote crossed with a recessive homozygote',
              blocks: [
                {
                  kind: 'table',
                  caption: 'Aa × aa',
                  headers: [
                    'First parent’s allele',
                    'Second parent’s allele',
                    'Offspring',
                    'Probability',
                  ],
                  rows: [
                    ['A', 'a', 'Aa', '1/2'],
                    ['a', 'a', 'aa', '1/2'],
                  ],
                },
                {
                  kind: 'paragraph',
                  text: 'Half the offspring are expected to be Aa and half aa in a large collection under this model. Two offspring are not guaranteed to include one of each. Each conception samples the parental possibilities anew.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'For Aa × Aa, the ordered combinations are AA, Aa, aA and aa, each with probability one quarter. Aa and aA describe the same genotype when parental origin has no effect, so the genotype probabilities are one quarter AA, one half Aa and one quarter aa. With complete dominance, combine the first three outcomes to obtain a three-quarters dominant phenotype probability.',
            },
            {
              kind: 'paragraph',
              text: 'Keep the genotype calculation separate from the phenotype rule. If dominance is incomplete or both alleles are visibly expressed, the same genotype probabilities may lead to a different phenotype ratio. Changing expression does not automatically change segregation.',
            },
          ],
        },
        {
          id: 'inference',
          title: 'Use observations without overclaiming',
          blocks: [
            {
              kind: 'paragraph',
              text: 'A test cross pairs an organism with the dominant phenotype but unknown genotype with an aa organism. If the unknown parent is AA, all offspring are Aa. If it is Aa, each offspring has a one-half chance of aa. An aa offspring therefore rules out AA for the unknown parent under the stated model.',
            },
            {
              kind: 'paragraph',
              text: 'However, a finite run of dominant-phenotype offspring does not prove AA. An Aa parent could happen to transmit A repeatedly. Three dominant offspring in this test cross have probability (1/2)³ = 1/8 if the unknown parent is Aa. The observation is informative, but it does not logically exclude that genotype.',
            },
            {
              kind: 'example',
              title: 'Condition on what was observed',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'In an Aa × Aa cross, suppose an offspring is known to have the dominant phenotype. The aa possibility is excluded. Of the three equally likely remaining parental combinations, two are heterozygous. Its conditional probability of being Aa is therefore 2/3, rather than the unconditional 1/2.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'Conditioning changes the population being counted. It does not change the original genetic mechanism. Similarly, prior offspring do not force later offspring to fill an expected ratio if conceptions are independent conditional on parental genotypes. Probability describes a distribution of possible families, not a promise of exact balance within every small family.',
            },
          ],
        },
        {
          id: 'limits',
          title: 'The model has explicit boundaries',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Dominance describes expression in a heterozygote, not transmission frequency or evolutionary success. An allele can be rare and dominant, common and recessive, harmful in one environment or helpful in another. The one-gene model supplies no population frequency unless additional information about mating and allele prevalence is given.',
            },
            {
              kind: 'paragraph',
              text: 'Incomplete dominance means a heterozygote has an intermediate phenotype in the stated trait. Codominance means both allele-associated features are expressed. Neither means that the alleles permanently blend into a new heritable allele. When gametes form, the alleles can still segregate. The genotype-to-phenotype table must be changed deliberately when the expression rule changes.',
            },
            {
              kind: 'paragraph',
              text: 'Many human and other organismal traits involve multiple genes and environmental influences. The simple A/a model is therefore a controlled teaching model, not a universal account of appearance or health. The daily questions explicitly assume one autosomal locus, ordinary segregation, random fertilization and the stated expression pattern; mutation and unequal survival are excluded where necessary.',
            },
            {
              kind: 'paragraph',
              text: 'To solve a new cross, first write possible parental genotypes, then list gamete probabilities, combine parental contributions, and only then translate genotypes into phenotypes. Check that probabilities sum to one. If an observation narrows the possible offspring, restrict to that subset and renormalize. This sequence prevents confusing a displayed trait with a known genotype or interpreting a three-to-one ratio as four births that must come in a prescribed order.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b2-gen-1',
        prompt:
          'At one diploid locus with complete dominance, A is dominant to a. An organism shows the dominant phenotype. Which genotype conclusion follows from appearance alone?',
        options: [
          {
            id: '0',
            text: 'It must be AA.',
          },
          {
            id: '1',
            text: 'It must be Aa.',
          },
          {
            id: '2',
            text: 'It must transmit A to every offspring.',
          },
          {
            id: '3',
            text: 'It could be AA or Aa.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'Both AA and Aa express the dominant phenotype, so appearance alone cannot distinguish them.',
        sectionIds: ['alleles'],
        evidence:
          'Both AA and Aa express the dominant phenotype, so appearance alone cannot distinguish them.',
      },
      {
        id: 'b2-gen-2',
        prompt:
          'Under ordinary Mendelian segregation, what allele probabilities does an Aa parent contribute at this locus?',
        options: [
          {
            id: '0',
            text: 'A with probability 3/4 and a with probability 1/4',
          },
          {
            id: '1',
            text: 'A with probability 1/2 and a with probability 1/2',
          },
          {
            id: '2',
            text: 'Only A, because it is dominant',
          },
          {
            id: '3',
            text: 'Both A and a in every gamete',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'A diploid heterozygote contributes one allele per gamete, with equal segregation in this model.',
        sectionIds: ['alleles', 'gametes'],
        evidence:
          'A diploid heterozygote contributes one allele per gamete, with equal segregation in this model.',
      },
      {
        id: 'b2-gen-3',
        prompt:
          'Assume random fertilization and equal gamete probabilities. In Aa × Aa, what is the probability of heterozygous offspring?',
        options: [
          {
            id: '0',
            text: '1/4',
          },
          {
            id: '1',
            text: '3/4',
          },
          {
            id: '2',
            text: '1/2',
          },
          {
            id: '3',
            text: '1',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Aa arises from A-a or a-A, two of the four equally likely parental combinations.',
        sectionIds: ['alleles', 'gametes'],
        evidence:
          'Aa arises from A-a or a-A, two of the four equally likely parental combinations.',
      },
      {
        id: 'b2-gen-4',
        prompt:
          'For Aa × Aa with complete dominance, an offspring is known to display the dominant phenotype. What is the probability it is heterozygous?',
        options: [
          {
            id: '0',
            text: '2/3',
          },
          {
            id: '1',
            text: '1/2',
          },
          {
            id: '2',
            text: '1/3',
          },
          {
            id: '3',
            text: '3/4',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Excluding aa leaves AA, Aa and aA; two of the three remaining combinations are heterozygous.',
        sectionIds: ['alleles', 'gametes', 'inference'],
        evidence:
          'Excluding aa leaves AA, Aa and aA; two of the three remaining combinations are heterozygous.',
      },
      {
        id: 'b2-gen-5',
        prompt:
          'An unknown dominant-phenotype plant is crossed with aa and produces an aa offspring. With no mutation or unusual segregation, what must the unknown parent be?',
        options: [
          {
            id: '0',
            text: 'AA',
          },
          {
            id: '1',
            text: 'aa',
          },
          {
            id: '2',
            text: 'Aa',
          },
          {
            id: '3',
            text: 'Either AA or Aa remains possible',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'An aa offspring needs an a contribution from each parent; a dominant-phenotype parent carrying a must be Aa.',
        sectionIds: ['alleles', 'inference'],
        evidence:
          'An aa offspring needs an a contribution from each parent; a dominant-phenotype parent carrying a must be Aa.',
      },
      {
        id: 'b2-gen-6',
        prompt:
          'An unknown dominant-phenotype plant crossed with aa has three dominant-phenotype offspring. Which conclusion is justified under the same model?',
        options: [
          {
            id: '0',
            text: 'The unknown parent is certainly AA.',
          },
          {
            id: '1',
            text: 'The unknown parent is certainly Aa.',
          },
          {
            id: '2',
            text: 'The fourth offspring must be recessive.',
          },
          {
            id: '3',
            text: 'Both AA and Aa remain possible.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'An Aa parent can transmit A three times by chance; finite dominant offspring do not exclude heterozygosity.',
        sectionIds: ['alleles', 'gametes', 'inference'],
        evidence:
          'An Aa parent can transmit A three times by chance; finite dominant offspring do not exclude heterozygosity.',
      },
      {
        id: 'b2-gen-7',
        prompt:
          'Two Aa parents have already produced three aa offspring. Assuming independent conceptions and the same one-locus model, what is the next offspring’s probability of aa?',
        options: [
          {
            id: '0',
            text: '0, because the ratio must balance',
          },
          {
            id: '1',
            text: '1/4',
          },
          {
            id: '2',
            text: '1/2',
          },
          {
            id: '3',
            text: '3/4',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Conditional on fixed parental genotypes, prior offspring do not change the next conception’s allele probabilities.',
        sectionIds: ['alleles', 'gametes', 'inference'],
        evidence:
          'Conditional on fixed parental genotypes, prior offspring do not change the next conception’s allele probabilities.',
      },
      {
        id: 'b2-gen-8',
        prompt:
          'Which statement about a dominant allele follows from the meaning of dominance alone?',
        options: [
          {
            id: '0',
            text: 'It determines the stated phenotype in the heterozygote under complete dominance.',
          },
          {
            id: '1',
            text: 'It is more common in the population.',
          },
          {
            id: '2',
            text: 'It improves survival.',
          },
          {
            id: '3',
            text: 'It is transmitted more than half the time by a heterozygote.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Dominance concerns expression, not frequency, fitness or biased transmission.',
        sectionIds: ['alleles', 'limits'],
        evidence: 'Dominance concerns expression, not frequency, fitness or biased transmission.',
      },
      {
        id: 'b2-gen-9',
        prompt:
          'For an incompletely dominant trait, RR is red, rr is white and Rr is pink. What phenotype ratio is expected from Rr × Rr under ordinary segregation?',
        options: [
          {
            id: '0',
            text: '3 red : 1 white',
          },
          {
            id: '1',
            text: '1 red : 2 pink : 1 white',
          },
          {
            id: '2',
            text: 'All pink',
          },
          {
            id: '3',
            text: '1 red : 1 white',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'The 1:2:1 genotype distribution translates into three distinct phenotypes under incomplete dominance.',
        sectionIds: ['alleles', 'gametes', 'limits'],
        evidence:
          'The 1:2:1 genotype distribution translates into three distinct phenotypes under incomplete dominance.',
      },
      {
        id: 'b2-gen-10',
        prompt:
          'At a single autosomal locus, AA is crossed with aa. Assuming ordinary segregation, random fertilization and no mutation, what genotypes occur?',
        options: [
          {
            id: '0',
            text: 'Half AA and half aa',
          },
          {
            id: '1',
            text: 'Three-quarters Aa and one-quarter aa',
          },
          {
            id: '2',
            text: 'All Aa',
          },
          {
            id: '3',
            text: 'One-quarter AA, one-half Aa and one-quarter aa',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'Every gamete from AA contributes A and every gamete from aa contributes a, so every offspring is Aa.',
        sectionIds: ['alleles', 'gametes'],
        evidence:
          'Every gamete from AA contributes A and every gamete from aa contributes a, so every offspring is Aa.',
      },
    ],
  },
  {
    id: 'foundations-arguments',
    category: 'foundations',
    title: 'Claims, conditions, and arguments',
    topic: 'Deductive reasoning and evidence',
    difficulty: 'Challenging',
    reading: {
      title: 'What follows from a claim?',
      sources: [
        {
          title: 'OpenStax · Arguments (CC BY 4.0)',
          url: 'https://openstax.org/books/introduction-philosophy/pages/5-3-arguments',
        },
        {
          title: 'OpenStax Introduction to Philosophy · Types of Inferences',
          url: 'https://openstax.org/books/introduction-philosophy/pages/5-4-types-of-inferences',
          note: 'Reviewed conditional inference forms and induction; CC BY 4.0.',
        },
        {
          title: 'OpenStax Contemporary Mathematics · Equivalent Statements',
          url: 'https://openstax.org/books/contemporary-mathematics/pages/2-5-equivalent-statements',
          note: 'Reviewed conditional equivalence and contrapositive; CC BY 4.0.',
        },
      ],
      sections: [
        {
          id: 'claims',
          title: 'Separate reasons from the claim they support',
          blocks: [
            {
              kind: 'paragraph',
              text: 'An argument is a set of reasons offered for a conclusion. Its premises are the claims doing the supporting; its conclusion is the claim being supported. An argument need not involve a disagreement or raised voices. “Every item in this box is fragile; this vase is in the box; therefore the vase is fragile” is an argument because it links premises to a conclusion.',
            },
            {
              kind: 'paragraph',
              text: 'A deductively valid argument cannot have all true premises and a false conclusion. Validity concerns the connection between statements, not whether the premises happen to be true. An argument is sound when it is valid and its premises are true. An invalid argument may still have a true conclusion; it simply has not established that conclusion by the offered reasoning.',
            },
            {
              kind: 'paragraph',
              text: 'One way to test validity is to seek a counterexample: a possible situation in which all the premises hold while the conclusion fails. One such situation is enough to disprove validity. Failing to imagine one immediately is not a proof of validity, but constructing one can expose a hidden leap. For universal claims, a single counterexample also disproves “all,” whereas many confirming examples do not establish an unrestricted universal statement.',
            },
          ],
        },
        {
          id: 'conditions',
          title: 'If, only if, and their directions',
          blocks: [
            {
              kind: 'paragraph',
              text: 'The conditional “if P, then Q” says that P is sufficient for Q and Q is necessary for P. Whenever P holds, Q must hold. It does not say that Q occurs only through P. For example, if a shape is a square, it is a rectangle. Being a square is sufficient for being a rectangle; being a rectangle is necessary for being a square. A rectangle can nevertheless fail to be square.',
            },
            {
              kind: 'table',
              caption: 'Four related statements',
              headers: ['Form', 'Relation to “if P then Q”'],
              rows: [
                ['If P then Q', 'Original conditional'],
                ['If not Q then not P', 'Contrapositive; equivalent'],
                ['If Q then P', 'Converse; not generally equivalent'],
                ['If not P then not Q', 'Inverse; not generally equivalent'],
              ],
            },
            {
              kind: 'paragraph',
              text: '“P only if Q” puts Q on the necessary side: P requires Q. “P if Q” says Q is sufficient for P. “P if and only if Q” combines both directions. Reading these phrases carefully prevents reversing a requirement into a guarantee.',
            },
            {
              kind: 'example',
              title: 'A museum rule',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Suppose entering a restricted room requires a badge. This says entry implies a badge. Someone without a badge cannot satisfy the stated entry requirement. But having a badge alone need not guarantee entry: a reservation or an escort might also be required. The necessary condition does not claim to be sufficient.',
                },
              ],
            },
          ],
        },
        {
          id: 'inference',
          title: 'Which conditional moves are valid?',
          blocks: [
            {
              kind: 'paragraph',
              text: 'From “if P then Q” and P, infer Q. This is often called modus ponens. From the same conditional and not Q, infer not P; this is modus tollens and uses the contrapositive. Both preserve truth because they rule out exactly the combination the conditional forbids: P true with Q false.',
            },
            {
              kind: 'paragraph',
              text: 'From Q alone, however, inferring P affirms the consequent and is invalid in general. From not P, inferring not Q denies the antecedent and is also invalid in general. A wet street could result from rain or a cleaning truck. If rain implies a wet street, observing wetness does not identify rain, and absence of rain does not establish dryness.',
            },
            {
              kind: 'example',
              title: 'A concrete counterexample',
              blocks: [
                {
                  kind: 'paragraph',
                  text: 'Assume every oak in a garden is a tree. A birch in the garden is a tree but not an oak. It satisfies the conclusion of the conditional without satisfying its premise, so it disproves the proposed inference “tree, therefore oak.” No botanical dispute is needed; the example makes the logical direction visible.',
                },
              ],
            },
            {
              kind: 'paragraph',
              text: 'The word some means at least one in the logical claims used here. “Some A are B” does not mean all A are B or exactly one is. Two separate existence claims need not concern the same individual. If some readers are musicians and some readers are cyclists, it does not follow that any musician is a cyclist: the witnesses could be different readers.',
            },
          ],
        },
        {
          id: 'evidence',
          title: 'Deduction is not every kind of reasoning',
          blocks: [
            {
              kind: 'paragraph',
              text: 'Inductive reasoning supports a conclusion as more or less likely rather than guaranteeing it from the premises. A representative survey can give evidence about an unsurveyed population; a successful prediction can strengthen a scientific explanation. Such reasoning is not defective merely because it lacks deductive certainty. Its quality depends on evidence, alternatives, and the strength of the conclusion actually drawn.',
            },
            {
              kind: 'paragraph',
              text: 'An explanation asks why an accepted event occurred, while an argument offers reasons to accept a claim. A passage can do both, but distinguishing these functions helps identify what needs support. “The street is wet because it rained” gives an explanation only if the relevant facts and causal connection are warranted; the word because does not automatically establish them.',
            },
            {
              kind: 'paragraph',
              text: 'The daily questions use explicit invented rules so you can assess the inference rather than debate background exceptions. If a rule says every archived file has a backup, a file with no backup cannot be archived under that rule. A backed-up file might still be unarchived. Keep the domain fixed: a rule about one system does not automatically describe every real archive.',
            },
            {
              kind: 'paragraph',
              text: 'When evaluating a new passage, write the premises and conclusion separately, preserve quantifiers such as all or some, and check whether a conditional has been reversed. Then ask whether the premises themselves are credible. Validity and premise truth are different checks, and both matter for a sound deductive argument. For an inductive claim, ask instead whether the evidence supports the level of confidence asserted and whether competing explanations remain plausible.',
            },
          ],
        },
      ],
      authorship: 'Original explanations prepared with AI assistance. References are listed below.',
    },
    questions: [
      {
        id: 'b2-arg-1',
        prompt:
          'Assume every archived file has a backup. A particular file has no backup. What follows under this rule?',
        options: [
          {
            id: '0',
            text: 'It is archived but damaged.',
          },
          {
            id: '1',
            text: 'It is not archived.',
          },
          {
            id: '2',
            text: 'It was never created.',
          },
          {
            id: '3',
            text: 'Nothing follows about whether it is archived.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'This is the contrapositive: archived implies backed up, so not backed up implies not archived.',
        sectionIds: ['conditions', 'inference'],
        evidence:
          'This is the contrapositive: archived implies backed up, so not backed up implies not archived.',
      },
      {
        id: 'b2-arg-2',
        prompt:
          'Assume every archived file has a backup. A file has a backup. Can we infer that it is archived?',
        options: [
          {
            id: '0',
            text: 'Yes, because the rule links the two properties.',
          },
          {
            id: '1',
            text: 'Yes, unless the backup is old.',
          },
          {
            id: '2',
            text: 'No, because backup is impossible for archived files.',
          },
          {
            id: '3',
            text: 'No, because unarchived files may also have backups.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The converse is not supplied; having the necessary condition does not establish the sufficient one.',
        sectionIds: ['conditions', 'inference'],
        evidence:
          'The converse is not supplied; having the necessary condition does not establish the sufficient one.',
      },
      {
        id: 'b2-arg-3',
        prompt:
          'A pass is required to enter a room, but the rule says nothing about other requirements. What role does the pass play?',
        options: [
          {
            id: '0',
            text: 'A necessary condition, not established as sufficient',
          },
          {
            id: '1',
            text: 'A sufficient condition, not necessary',
          },
          {
            id: '2',
            text: 'A condition both necessary and sufficient',
          },
          {
            id: '3',
            text: 'Neither necessary nor sufficient',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'Required means necessary. Other conditions may also be needed, so sufficiency is not established.',
        sectionIds: ['conditions'],
        evidence:
          'Required means necessary. Other conditions may also be needed, so sufficiency is not established.',
      },
      {
        id: 'b2-arg-4',
        prompt: 'Which situation proves a deductive argument invalid?',
        options: [
          {
            id: '0',
            text: 'Its conclusion is unpopular.',
          },
          {
            id: '1',
            text: 'One premise is false in the actual world.',
          },
          {
            id: '2',
            text: 'Its premises could all be true while its conclusion is false.',
          },
          {
            id: '3',
            text: 'Its conclusion happens to be true.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'A counterexample to truth preservation proves invalidity; actual premise falsity alone does not determine form.',
        sectionIds: ['claims'],
        evidence:
          'A counterexample to truth preservation proves invalidity; actual premise falsity alone does not determine form.',
      },
      {
        id: 'b2-arg-5',
        prompt: 'An argument is valid, but one premise is false. Which description is correct?',
        options: [
          {
            id: '0',
            text: 'It is sound because its form is valid.',
          },
          {
            id: '1',
            text: 'It is not sound, though its conclusion might be true.',
          },
          {
            id: '2',
            text: 'Its conclusion must be false.',
          },
          {
            id: '3',
            text: 'It becomes an inductive argument.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Soundness requires validity and true premises. False premises do not force a false conclusion.',
        sectionIds: ['claims'],
        evidence:
          'Soundness requires validity and true premises. False premises do not force a false conclusion.',
      },
      {
        id: 'b2-arg-6',
        prompt:
          'Some club readers are musicians, and some club readers are cyclists. What follows?',
        options: [
          {
            id: '0',
            text: 'Some musicians must be cyclists.',
          },
          {
            id: '1',
            text: 'All cyclists are readers.',
          },
          {
            id: '2',
            text: 'No musician can be a cyclist.',
          },
          {
            id: '3',
            text: 'The premises do not establish whether any musician is a cyclist.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          'The two existence claims may be witnessed by different people, so overlap is not forced or excluded.',
        sectionIds: ['inference'],
        evidence:
          'The two existence claims may be witnessed by different people, so overlap is not forced or excluded.',
      },
      {
        id: 'b2-arg-7',
        prompt: 'Which statement is equivalent to “If an item is approved, it is logged”?',
        options: [
          {
            id: '0',
            text: 'If it is logged, it is approved.',
          },
          {
            id: '1',
            text: 'If it is not approved, it is not logged.',
          },
          {
            id: '2',
            text: 'If it is not logged, it is not approved.',
          },
          {
            id: '3',
            text: 'An item is approved if and only if it is logged.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '2',
        },
        explanation:
          'The contrapositive is equivalent; the converse and inverse add unsupported implications.',
        sectionIds: ['conditions'],
        evidence:
          'The contrapositive is equivalent; the converse and inverse add unsupported implications.',
      },
      {
        id: 'b2-arg-8',
        prompt:
          'Which single finding would refute the claim “All samples in this tray contain copper”?',
        options: [
          {
            id: '0',
            text: 'One tray sample that contains no copper',
          },
          {
            id: '1',
            text: 'Many tray samples containing copper',
          },
          {
            id: '2',
            text: 'A copper-free sample in another tray',
          },
          {
            id: '3',
            text: 'A sample containing both copper and zinc',
          },
        ],
        answer: {
          kind: 'choice',
          value: '0',
        },
        explanation:
          'A universal claim about this tray is refuted by one member of its domain lacking the claimed property.',
        sectionIds: ['claims'],
        evidence:
          'A universal claim about this tray is refuted by one member of its domain lacking the claimed property.',
      },
      {
        id: 'b2-arg-9',
        prompt:
          'A representative survey supports a claim about unsurveyed residents but does not logically guarantee it. What kind of support is this?',
        options: [
          {
            id: '0',
            text: 'An invalid claim solely because certainty is absent',
          },
          {
            id: '1',
            text: 'Inductive support whose strength depends on the evidence',
          },
          {
            id: '2',
            text: 'A deductively sound proof by definition',
          },
          {
            id: '3',
            text: 'A contradiction between the sample and population',
          },
        ],
        answer: {
          kind: 'choice',
          value: '1',
        },
        explanation:
          'Induction can rationally support a conclusion without deductive certainty; sampling quality matters.',
        sectionIds: ['evidence'],
        evidence:
          'Induction can rationally support a conclusion without deductive certainty; sampling quality matters.',
      },
      {
        id: 'b2-arg-10',
        prompt:
          '“A request is accepted only if it is signed.” Which translation preserves the direction?',
        options: [
          {
            id: '0',
            text: 'If signed, then accepted.',
          },
          {
            id: '1',
            text: 'If not accepted, then not signed.',
          },
          {
            id: '2',
            text: 'If signed, then not accepted.',
          },
          {
            id: '3',
            text: 'If accepted, then signed.',
          },
        ],
        answer: {
          kind: 'choice',
          value: '3',
        },
        explanation:
          '“Only if” introduces the necessary condition: acceptance requires a signature, not conversely.',
        sectionIds: ['conditions'],
        evidence:
          '“Only if” introduces the necessary condition: acceptance requires a signature, not conversely.',
      },
    ],
  },
];
